import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShoppingCartIcon, PlusIcon, MinusIcon, TrashIcon, AlertCircleIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart, CartItem } from './CartContext';
export const CartSidebar: React.FC = () => {
  const {
    items,
    isCartOpen,
    closeCart,
    removeItem,
    updateQuantity,
    totalItems,
    totalPrice
  } = useCart();
  const cartRef = useRef<HTMLDivElement>(null);
  // Handle click outside to close cart
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cartRef.current && !cartRef.current.contains(event.target as Node) && isCartOpen) {
        closeCart();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    // Prevent scrolling when cart is open
    if (isCartOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isCartOpen, closeCart]);
  // Handle escape key to close cart
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isCartOpen) {
        closeCart();
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isCartOpen, closeCart]);
  const CartItem: React.FC<{
    item: CartItem;
  }> = ({
    item
  }) => {
    return <div className="flex py-4 border-b border-gray-100 dark:border-[#1A3330]" data-id="element-211">
        <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-md" data-id="element-212">
          <img src={item.image} alt={item.title} className="h-full w-full object-cover object-center" data-id="element-213" />
        </div>
        <div className="ml-4 flex flex-1 flex-col" data-id="element-214">
          <div className="flex justify-between text-base font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-215">
            <h3 className="line-clamp-1" data-id="element-216">{item.title}</h3>
            <p className="ml-4" data-id="element-217">${(item.price * item.quantity).toFixed(2)}</p>
          </div>
          <div className="flex items-center justify-between mt-auto" data-id="element-218">
            <div className="flex items-center border border-gray-200 dark:border-[#1A3330] rounded-md" data-id="element-219">
              <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="p-1 text-gray-500 dark:text-[#B0C1BD] hover:text-gray-700 dark:hover:text-white" data-id="element-220">
                <MinusIcon className="h-4 w-4" data-id="element-221" />
              </button>
              <span className="px-2 text-gray-700 dark:text-[#F8FAF9]" data-id="element-222">
                {item.quantity}
              </span>
              <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="p-1 text-gray-500 dark:text-[#B0C1BD] hover:text-gray-700 dark:hover:text-white" data-id="element-223">
                <PlusIcon className="h-4 w-4" data-id="element-224" />
              </button>
            </div>
            <button onClick={() => removeItem(item.id)} className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" data-id="element-225">
              <TrashIcon className="h-4 w-4" data-id="element-226" />
            </button>
          </div>
        </div>
      </div>;
  };
  const EmptyCart = () => <div className="flex flex-col items-center justify-center h-full py-12" data-id="element-227">
      <div className="w-16 h-16 bg-gray-100 dark:bg-[#1A3330] rounded-full flex items-center justify-center mb-4" data-id="element-228">
        <ShoppingCartIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A]" data-id="element-229" />
      </div>
      <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-230">
        Your cart is empty
      </h3>
      <p className="text-sm text-gray-500 dark:text-[#B0C1BD] text-center mb-6" data-id="element-231">
        Looks like you haven't added any products to your cart yet.
      </p>
      <Link to="/browse" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] hover:shadow-lg transition-all duration-300" onClick={closeCart} data-id="element-232">
        Browse Products
      </Link>
    </div>;
  return <AnimatePresence data-id="element-233">
      {isCartOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" data-id="element-234" />
          {/* Cart sidebar */}
          <motion.div ref={cartRef} initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'tween',
        duration: 0.3
      }} className="fixed right-0 top-0 h-full w-full sm:w-96 bg-white dark:bg-[#162825] shadow-xl z-50 flex flex-col" data-id="element-235">
            <div className="px-4 py-6 border-b border-gray-200 dark:border-[#1A3330] flex items-center justify-between" data-id="element-236">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center" data-id="element-237">
                <ShoppingCartIcon className="h-5 w-5 mr-2" data-id="element-238" />
                Your Cart
                {totalItems > 0 && <span className="ml-2 text-sm bg-[#2DB489] dark:bg-[#4ED1A0] text-white rounded-full px-2 py-0.5" data-id="element-239">
                    {totalItems}
                  </span>}
              </h2>
              <button onClick={closeCart} className="p-2 text-gray-500 dark:text-[#B0C1BD] hover:text-gray-700 dark:hover:text-white rounded-full hover:bg-gray-100 dark:hover:bg-[#1A3330] transition-colors" data-id="element-240">
                <XIcon className="h-5 w-5" data-id="element-241" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4" data-id="element-242">
              {items.length > 0 ? <div className="py-4" data-id="element-243">
                  {items.map(item => <CartItem key={item.id} item={item} data-id="element-244" />)}
                </div> : <EmptyCart data-id="element-245" />}
            </div>
            {items.length > 0 && <div className="border-t border-gray-200 dark:border-[#1A3330] px-4 py-6" data-id="element-246">
                <div className="flex justify-between text-base font-medium text-gray-900 dark:text-white mb-4" data-id="element-247">
                  <p data-id="element-248">Subtotal</p>
                  <p data-id="element-249">${totalPrice.toFixed(2)}</p>
                </div>
                <div className="flex flex-col gap-3" data-id="element-250">
                  <Link to="/checkout" onClick={closeCart} className="w-full flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] hover:shadow-lg transition-all" data-id="element-251">
                    Proceed to Checkout
                  </Link>
                  <button onClick={closeCart} className="w-full flex items-center justify-center px-6 py-3 border border-gray-300 dark:border-[#1A3330] rounded-md shadow-sm text-base font-medium text-gray-700 dark:text-[#F8FAF9] bg-white dark:bg-transparent hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors" data-id="element-252">
                    Continue Shopping
                  </button>
                </div>
                <div className="mt-4 flex items-center text-sm text-gray-500 dark:text-[#B0C1BD]" data-id="element-253">
                  <AlertCircleIcon className="h-4 w-4 mr-1.5 flex-shrink-0" data-id="element-254" />
                  <p data-id="element-255">Shipping and taxes calculated at checkout</p>
                </div>
              </div>}
          </motion.div>
        </>}
    </AnimatePresence>;
};